// ============================================
// TextureManager - Texture loading and caching
// ============================================

import { type IRenderContext } from '../core/IRenderContext';
import { type TextureHandle, type TextureDescriptor, NULL_HANDLE } from '../types';

/**
 * Built-in texture names for standard textures created on-demand.
 */
export type BuiltinTexture = 'white' | 'black' | 'checkerboard' | 'normal' | 'missing';

/**
 * Region within a texture (for atlas support)
 */
export interface TextureRegion {
  texture: TextureHandle;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  width: number;
  height: number;
}

/**
 * Sprite frame for animation
 */
export interface SpriteFrame {
  region: TextureRegion;
  duration: number; // ms
}

/**
 * Animation definition
 */
export interface Animation {
  name: string;
  frames: SpriteFrame[];
  loop: boolean;
}

/**
 * TextureManager - Load and manage textures
 */
export class TextureManager {
  private ctx: IRenderContext;
  private textures: Map<string, TextureHandle> = new Map();
  private regions: Map<string, TextureRegion> = new Map();
  private animations: Map<string, Animation> = new Map();
  private pending: Map<string, Promise<TextureHandle>> = new Map();
  
  constructor(ctx: IRenderContext) {
    this.ctx = ctx;
  }
  
  // ==========================================================================
  // LOADING
  // ==========================================================================
  
  /**
   * Load texture from URL
   */
  async loadUrl(name: string, url: string, options?: Partial<TextureDescriptor>): Promise<TextureHandle> {
    // Return cached
    if (this.textures.has(name)) {
      return this.textures.get(name)!;
    }
    
    // Return pending
    if (this.pending.has(name)) {
      return this.pending.get(name)!;
    }
    
    // Start loading
    const promise = this.loadUrlInternal(name, url, options);
    this.pending.set(name, promise);
    
    try {
      const handle = await promise;
      this.pending.delete(name);
      return handle;
    } catch (e) {
      this.pending.delete(name);
      throw e;
    }
  }
  
  private async loadUrlInternal(name: string, url: string, options?: Partial<TextureDescriptor>): Promise<TextureHandle> {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load texture: ${url}`));
      img.src = url;
    });
    
    return this.loadImage(name, img, options);
  }
  
  /**
   * Load texture from Image/Canvas
   */
  loadImage(name: string, source: TexImageSource, options?: Partial<TextureDescriptor>): TextureHandle {
    if (this.textures.has(name)) {
      return this.textures.get(name)!;
    }
    
    const handle = this.ctx.createTextureFromImage(source, {
      filter: 'nearest',
      wrapU: 'clamp',
      wrapV: 'clamp',
      ...options,
    });
    
    this.textures.set(name, handle);
    
    // Create default region
    const size = this.ctx.getTextureSize(handle);
    if (size) {
      this.regions.set(name, {
        texture: handle,
        u0: 0,
        v0: 0,
        u1: 1,
        v1: 1,
        width: size.width,
        height: size.height,
      });
    }
    
    return handle;
  }
  
  /**
   * Create texture from canvas
   */
  loadCanvas(name: string, canvas: HTMLCanvasElement, options?: Partial<TextureDescriptor>): TextureHandle {
    return this.loadImage(name, canvas, options);
  }
  
  /**
   * Create empty texture
   */
  create(name: string, width: number, height: number, options?: Partial<TextureDescriptor>): TextureHandle {
    if (this.textures.has(name)) {
      return this.textures.get(name)!;
    }
    
    const handle = this.ctx.createTexture({
      width,
      height,
      format: 'rgba8unorm',
      filter: 'nearest',
      ...options,
    });
    
    this.textures.set(name, handle);
    this.regions.set(name, {
      texture: handle,
      u0: 0,
      v0: 0,
      u1: 1,
      v1: 1,
      width,
      height,
    });
    
    return handle;
  }
  
  /**
   * Update texture data
   */
  update(name: string, data: ArrayBufferView | TexImageSource): void {
    const handle = this.textures.get(name);
    if (handle === undefined) return;
    this.ctx.updateTexture(handle, data);
  }
  
  // ==========================================================================
  // ATLAS / REGIONS
  // ==========================================================================
  
  /**
   * Define a region within a texture
   */
  defineRegion(
    name: string,
    textureName: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): TextureRegion | null {
    const handle = this.textures.get(textureName);
    if (handle === undefined) return null;
    
    const texSize = this.ctx.getTextureSize(handle);
    if (!texSize) return null;
    
    const region: TextureRegion = {
      texture: handle,
      u0: x / texSize.width,
      v0: y / texSize.height,
      u1: (x + width) / texSize.width,
      v1: (y + height) / texSize.height,
      width,
      height,
    };
    
    this.regions.set(name, region);
    return region;
  }
  
  /**
   * Define grid of regions (sprite sheet)
   */
  defineGrid(
    textureName: string,
    prefix: string,
    cellWidth: number,
    cellHeight: number,
    cols: number,
    rows: number,
    startIndex = 0
  ): void {
    let index = startIndex;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const name = `${prefix}${index}`;
        this.defineRegion(name, textureName, col * cellWidth, row * cellHeight, cellWidth, cellHeight);
        index++;
      }
    }
  }
  
  /**
   * Get region by name
   */
  getRegion(name: string): TextureRegion | undefined {
    return this.regions.get(name);
  }
  
  // ==========================================================================
  // ANIMATION
  // ==========================================================================
  
  /**
   * Define animation from regions
   */
  defineAnimation(
    name: string,
    regionNames: string[],
    frameDuration: number,
    loop = true
  ): Animation | null {
    const frames: SpriteFrame[] = [];
    
    for (const regionName of regionNames) {
      const region = this.regions.get(regionName);
      if (!region) {
        console.warn(`Animation ${name}: region ${regionName} not found`);
        return null;
      }
      frames.push({ region, duration: frameDuration });
    }
    
    const anim: Animation = { name, frames, loop };
    this.animations.set(name, anim);
    return anim;
  }
  
  /**
   * Get animation by name
   */
  getAnimation(name: string): Animation | undefined {
    return this.animations.get(name);
  }
  
  // ==========================================================================
  // ACCESS
  // ==========================================================================
  
  /**
   * Get texture handle by name
   */
  get(name: string): TextureHandle {
    return this.textures.get(name) ?? NULL_HANDLE;
  }
  
  /**
   * Check if texture exists
   */
  has(name: string): boolean {
    return this.textures.has(name);
  }
  
  /**
   * Get texture size
   */
  getSize(name: string): { width: number; height: number } | null {
    const handle = this.textures.get(name);
    if (handle === undefined) return null;
    return this.ctx.getTextureSize(handle);
  }
  
  // ==========================================================================
  // BUILTIN TEXTURES (lazy-created standard textures)
  // ==========================================================================
  
  private builtins: Map<BuiltinTexture, TextureHandle> = new Map();
  
  /**
   * Get a built-in texture (created lazily on first request).
   * These are standard utility textures that are commonly needed.
   * 
   * @param name - Built-in texture name: 'white', 'black', 'checkerboard', 'normal', 'missing'
   * @returns Texture handle (cached, do NOT destroy it)
   * 
   * @example
   * ```typescript
   * const white = textures.getBuiltin('white'); // 1x1 white pixel
   * const checker = textures.getBuiltin('checkerboard'); // 8x8 debug texture
   * ```
   */
  getBuiltin(name: BuiltinTexture): TextureHandle {
    // Return cached
    const cached = this.builtins.get(name);
    if (cached !== undefined) return cached;
    
    // Create on-demand
    const handle = this.createBuiltinTexture(name);
    this.builtins.set(name, handle);
    return handle;
  }
  
  /**
   * Check if a builtin texture has been created.
   */
  hasBuiltin(name: BuiltinTexture): boolean {
    return this.builtins.has(name);
  }
  
  private createBuiltinTexture(name: BuiltinTexture): TextureHandle {
    switch (name) {
      case 'white':
        return this.createSolidTexture(`__builtin_${name}`, 255, 255, 255, 255);
        
      case 'black':
        return this.createSolidTexture(`__builtin_${name}`, 0, 0, 0, 255);
        
      case 'normal':
        // Flat normal (pointing up in tangent space: 0.5, 0.5, 1.0)
        return this.createSolidTexture(`__builtin_${name}`, 128, 128, 255, 255);
        
      case 'checkerboard':
        return this.createCheckerboardTexture(`__builtin_${name}`, 8);
        
      case 'missing':
        return this.createMissingTexture(`__builtin_${name}`);
        
      default:
        // Fallback to white
        return this.createSolidTexture(`__builtin_${name}`, 255, 255, 255, 255);
    }
  }
  
  private createSolidTexture(label: string, r: number, g: number, b: number, a: number): TextureHandle {
    const handle = this.ctx.createTexture({
      width: 1,
      height: 1,
      format: 'rgba8unorm',
      usage: ['sampled', 'copy-dst'],
      filter: 'nearest',
      label,
    });
    this.ctx.updateTexture(handle, new Uint8Array([r, g, b, a]));
    return handle;
  }
  
  private createCheckerboardTexture(label: string, size: number): TextureHandle {
    const data = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = (y * size + x) * 4;
        const white = (x + y) % 2 === 0;
        data[idx + 0] = white ? 255 : 128;
        data[idx + 1] = white ? 255 : 128;
        data[idx + 2] = white ? 255 : 128;
        data[idx + 3] = 255;
      }
    }
    
    const handle = this.ctx.createTexture({
      width: size,
      height: size,
      format: 'rgba8unorm',
      usage: ['sampled', 'copy-dst'],
      filter: 'nearest',
      label,
    });
    this.ctx.updateTexture(handle, data);
    return handle;
  }
  
  private createMissingTexture(label: string): TextureHandle {
    // 4x4 magenta/black checkerboard (standard "missing texture" pattern)
    const size = 4;
    const data = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = (y * size + x) * 4;
        const magenta = (x + y) % 2 === 0;
        data[idx + 0] = magenta ? 255 : 0;
        data[idx + 1] = 0;
        data[idx + 2] = magenta ? 255 : 0;
        data[idx + 3] = 255;
      }
    }
    
    const handle = this.ctx.createTexture({
      width: size,
      height: size,
      format: 'rgba8unorm',
      usage: ['sampled', 'copy-dst'],
      filter: 'nearest',
      label,
    });
    this.ctx.updateTexture(handle, data);
    return handle;
  }
  
  // ==========================================================================
  // CLEANUP
  // ==========================================================================
  
  /**
   * Destroy a texture
   */
  destroy(name: string): void {
    const handle = this.textures.get(name);
    if (handle !== undefined) {
      this.ctx.destroyTexture(handle);
      this.textures.delete(name);
    }
    this.regions.delete(name);
  }
  
  /**
   * Destroy all textures
   */
  destroyAll(): void {
    for (const [name, handle] of this.textures) {
      this.ctx.destroyTexture(handle);
    }
    this.textures.clear();
    this.regions.clear();
    this.animations.clear();
    this.pending.clear();
    
    // Also destroy built-in textures
    for (const [, handle] of this.builtins) {
      this.ctx.destroyTexture(handle);
    }
    this.builtins.clear();
  }
}
